import { AfterViewInit, Directive, EventEmitter, Host, Input, OnDestroy, Output, Renderer2 } from '@angular/core';

import { DziViewerComponent } from '../../dzi-viewer.component';
import { DziOverlaysViewerDirective, OverlayData } from '../dzi-overlays-viewer/dzi-overlays-viewer.directive';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import OpenSeadragon from 'openseadragon';
import { TOsdActiveAction } from '@telespot/shared/viewers/data-access';

@Directive({
    selector: '[tsDziOverlaysEditor]',
    standalone: false
})
export class DziOverlaysEditorDirective implements AfterViewInit, OnDestroy {
  @Input() overlays: OverlayData[] = [];
  @Input() viewerMode: TOsdActiveAction = TOsdActiveAction.selecting;
  @Output() overlaySelected = new EventEmitter<string[]>();
  @Output() overlayUpdated = new EventEmitter<OverlayData>();

  private corners = ['tl', 'tr', 'bl', 'br'];
  private startPoint: OpenSeadragon.Point | null = null;
  private tempOverlay: HTMLDivElement | null = null;
  private isDragOperation = false;
  private destroy$ = new Subject<void>();

  constructor(
    @Host() private dziViewer: DziViewerComponent,
    @Host() private overlaysRenderer: DziOverlaysViewerDirective,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    if (!this.overlaysRenderer) return;

    this.overlaysRenderer.overlaysRendered.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.addOverlaysHandlers();
    });

    const canvasTracker = new OpenSeadragon.MouseTracker({
      element: this.dziViewer['viewer'].canvas,

      pressHandler: (event: any) => {
        const viewer = this.dziViewer['viewer'];
        const isSelecting = this.viewerMode === TOsdActiveAction.selecting;

        if (!isSelecting) return;

        // Track that we started a potential drag operation
        this.isDragOperation = false;

        viewer.setMouseNavEnabled(false);

        this.startPoint = viewer.viewport.pointFromPixel(event.position, true);

        this.tempOverlay = this.renderer.createElement('div');
        this.renderer.setAttribute(this.tempOverlay, 'id', 'temp');
        this.renderer.setStyle(this.tempOverlay, 'border', '2px dashed white');
        this.renderer.setStyle(this.tempOverlay, 'position', 'absolute');

        viewer.addOverlay(this.tempOverlay, this.startPoint);
      },

      dragHandler: (event: any) => {
        const isSelecting = this.viewerMode === TOsdActiveAction.selecting;

        if (!isSelecting) return;

        if (!this.startPoint || !this.tempOverlay) return;

        this.isDragOperation = true;

        const viewer = this.dziViewer['viewer'];
        const { position } = event;
        const currentPoint = viewer.viewport.pointFromPixel(position, true);

        const x = Math.min(this.startPoint.x, currentPoint.x);
        const y = Math.min(this.startPoint.y, currentPoint.y);
        const width = Math.abs(currentPoint.x - this.startPoint.x);
        const height = Math.abs(currentPoint.y - this.startPoint.y);

        const rect = new OpenSeadragon.Rect(x, y, width, height);
        viewer.updateOverlay(this.tempOverlay, rect);
      },

      dragEndHandler: () => {
        const isSelecting = this.viewerMode === TOsdActiveAction.selecting;

        if (!isSelecting) return;

        if (!this.startPoint || !this.tempOverlay) return;

        const viewer = this.dziViewer['viewer'];

        const element = viewer.getOverlayById(this.tempOverlay.id);
        const bounds = element.getBounds(viewer.viewport);

        if (!bounds) return;

        const imageRect = viewer.viewport.viewportToImageRectangle(bounds);

        const selectedOverlays: OverlayData[] = [];

        for (const overlay of this.overlays) {
          const viewerOverlay = viewer.getOverlayById(overlay.id);

          if (!viewerOverlay) continue;

          const overlayBounds = viewerOverlay.getBounds(viewer.viewport);

          const overlayRect = viewer.viewport.viewportToImageRectangle(overlayBounds);

          const intersects =
            imageRect.x < overlayRect.x + overlayRect.width &&
            imageRect.x + imageRect.width > overlayRect.x &&
            imageRect.y < overlayRect.y + overlayRect.height &&
            imageRect.y + imageRect.height > overlayRect.y;

          if (!intersects) continue;

          selectedOverlays.push(overlay);
        }

        if (selectedOverlays.length === 0) {
          this.overlaySelected.emit([]);
        } else {
          this.overlaySelected.emit(selectedOverlays.map((o) => o.id));
        }

        viewer.removeOverlay(this.tempOverlay);
        this.tempOverlay = null;
        this.startPoint = null;

        viewer.setMouseNavEnabled(true);
      },

      releaseHandler: () => {
        const isSelecting = this.viewerMode === TOsdActiveAction.selecting;
        if (!isSelecting) return;

        if (!this.isDragOperation) {
          this.overlaySelected.emit([]);

          const viewer = this.dziViewer['viewer'];

          viewer.setMouseNavEnabled(true);

          if (this.tempOverlay) {
            viewer.removeOverlay(this.tempOverlay);
            this.tempOverlay = null;
            this.startPoint = null;
          }
        }

        this.isDragOperation = false;
      },
    });

    canvasTracker.setTracking(true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private addOverlaysHandlers() {
    for (const overlay of this.overlays) {
      const element = document.getElementById(overlay.id);

      if (!element) continue;

      const selectionTracker = new OpenSeadragon.MouseTracker({
        element,
        clickHandler: () => {
          if (overlay.selected) return;

          const viewer = this.dziViewer['viewer'];
          viewer.setMouseNavEnabled(true);

          const isSelecting = this.viewerMode === TOsdActiveAction.selecting;

          if (!isSelecting) return;

          this.overlaySelected.emit([overlay.id]);
        },
      });

      const movementTracker = new OpenSeadragon.MouseTracker({
        element,

        pressHandler: () => {
          if (!overlay.selected) return;
          this.renderer.setStyle(element, 'cursor', 'grabbing');
        },

        dragHandler: (event: any) => {
          if (!overlay.selected) return;

          const viewer = this.dziViewer['viewer'];
          const bounds = viewer.getOverlayById(overlay.id).getBounds(viewer.viewport);
          const offset = viewer.viewport.deltaPointsFromPixelsNoRotate(event.delta);

          const newBounds = bounds.translate(offset);
          viewer.updateOverlay(overlay.id, newBounds);
        },

        dragEndHandler: () => {
          if (!overlay.selected) return;

          const viewer = this.dziViewer['viewer'];
          const bounds = viewer.getOverlayById(overlay.id).getBounds(viewer.viewport);
          const imageRect = viewer.viewport.viewportToImageRectangle(bounds);

          this.overlayUpdated.emit({
            id: overlay.id,
            x: imageRect.x,
            y: imageRect.y,
            w: overlay.w,
            h: overlay.h,
          });
        },

        releaseHandler: () => {
          if (!overlay.selected) return;
          this.renderer.setStyle(element, 'cursor', 'grab');
        },
      });

      selectionTracker.setTracking(true);
      movementTracker.setTracking(overlay.selected);

      if (overlay.selected && overlay.w && overlay.h) {
        this.addResizeHandlers(element);
      }
    }
  }

  private addResizeHandlers(container: HTMLElement) {
    this.corners.forEach((corner) => {
      const handle = document.getElementById(`${container.id}-${corner}-handle`);
      if (!handle) return;

      const resizeTracker = new OpenSeadragon.MouseTracker({
        element: handle,

        dragHandler: (event: any) => {
          const viewer = this.dziViewer['viewer'];
          const bounds = viewer.getOverlayById(container.id).getBounds(viewer.viewport);
          const offset = viewer.viewport.deltaPointsFromPixelsNoRotate(event.delta);
          const newRect = bounds.clone();

          switch (corner) {
            case 'br':
              newRect.width += offset.x;
              newRect.height += offset.y;
              break;
            case 'tr':
              newRect.width += offset.x;
              newRect.y += offset.y;
              newRect.height -= offset.y;
              break;
            case 'bl':
              newRect.x += offset.x;
              newRect.width -= offset.x;
              newRect.height += offset.y;
              break;
            case 'tl':
              newRect.x += offset.x;
              newRect.y += offset.y;
              newRect.width -= offset.x;
              newRect.height -= offset.y;
              break;
          }

          if (newRect.width < 0.001 || newRect.height < 0.001) return;

          viewer.updateOverlay(container.id, newRect);
        },

        dragEndHandler: () => {
          const viewer = this.dziViewer['viewer'];
          const bounds = viewer.getOverlayById(container.id).getBounds(viewer.viewport);
          const imageRect = viewer.viewport.viewportToImageRectangle(bounds);

          this.overlayUpdated.emit({
            id: container.id,
            x: imageRect.x,
            y: imageRect.y,
            w: imageRect.width,
            h: imageRect.height,
          });
        },
      });

      resizeTracker.setTracking(true);
    });
  }
}
